import { resolveCablePolarity } from "./cable-color";
import { isAcTerminalId } from "./ac";
import { effectiveDimensions } from "./effective";
import { fuseExceedsTerminalLimit } from "./terminal-fuse-limit";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  SystemNode,
  Terminal,
  TerminalPolarity,
} from "./types";

export function isNegativeOrGround(
  polarity: TerminalPolarity | undefined,
): boolean {
  return polarity === "negative" || polarity === "ground";
}

function terminalIsNegative(t: Terminal | undefined): boolean {
  if (!t) return false;
  return isNegativeOrGround(t.polarity);
}

export function cableIsNegativePath(
  from: Terminal | TerminalPolarity | undefined,
  to: Terminal | TerminalPolarity | undefined,
): boolean {
  const check = (t: Terminal | TerminalPolarity | undefined): boolean => {
    if (t == null) return false;
    if (typeof t === "string") return isNegativeOrGround(t);
    return terminalIsNegative(t);
  };
  return check(from) || check(to);
}

export function isFuseBlock(def: ComponentDefinition | undefined): boolean {
  return !!def?.isFuseBlock;
}

export function isFuseBlockLike(def: ComponentDefinition | undefined): boolean {
  return !!def?.isFuseBlock;
}

/** A single series fuse, either the catalog symbol or an explicitly configured
 * two-post custom card. Its rating protects both attached conductors. */
export function isFuseHolderVariant(def: ComponentDefinition | undefined): boolean {
  if (!def?.isFuseBlock) return false;
  if (def.renderVariant === "fuse-holder") return true;
  return def.id === "custom" && !def.renderVariant && def.category === "distribution" &&
    !def.acVoltage && !def.simulateStates && !def.internalDcConnections &&
    !def.oneWayDcConnections && !def.currentShunt &&
    def.terminals.length === 2 && def.terminals.every(t => t.polarity === "positive" && !isAcTerminalId(t.id)) &&
    def.terminals.some(t => t.direction === "input") && def.terminals.some(t => t.direction === "output");
}

/** False for cranking conductors, which ABYC E-11.10 exempts from overcurrent
 *  protection, for AC runs, which the inlet's breaker protects (E-11.10.2 —
 *  never a fuse in the neutral alone), and for a run with nothing to rate
 *  against (`fuse === 0`: neither end resolves a current and no load is
 *  path-walked). The last used to be written as the ladder's 1 A rung. */
export function shouldAutoFuse(spec: {
  isCranking: boolean;
  isAc: boolean;
  fuse: number;
  sizingVerificationRequired?: boolean;
  fuseRecommendationUnavailableReason?: string;
  terminalFuseLimitA?: number;
}): boolean {
  return !spec.sizingVerificationRequired && !spec.fuseRecommendationUnavailableReason &&
    !fuseExceedsTerminalLimit(spec, spec.fuse) && !spec.isCranking && !spec.isAc && spec.fuse > 0;
}

export function absolutePosition(
  node: SystemNode,
  nodes: SystemNode[],
): { x: number; y: number } {
  let x = node.position.x;
  let y = node.position.y;
  let current = node;
  while (current.parentId) {
    const parent = nodes.find((n) => n.id === current.parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    current = parent;
  }
  return { x, y };
}

export function nodeCenter(
  node: ComponentNode,
  def: ComponentDefinition,
  nodes: SystemNode[],
): { x: number; y: number } {
  const abs = absolutePosition(node, nodes);
  const dims = effectiveDimensions(def, node);
  return { x: abs.x + dims.width / 2, y: abs.y + dims.height / 2 };
}

/** ABYC prohibits overcurrent protection in a negative or ground conductor. */
export function isFusablePositiveCable(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
): boolean {
  return resolveCablePolarity(cable, nodesById, cables) !== "negative";
}
